import { dateFromISOString } from '@wz-utilities/date.utilities';
import { SubscriptionApiModels } from '../../../../models/api/subscription-api.models';
import { CommonModels } from '../../../../models/common.models';
import { DocumentModels } from '../../../../models/document.models';
import { SubscriptionModels } from '../../../../models/subscription.models';
import { AddressProjector } from './address-projector';
import { DateProjector } from './date-projector';

export class SubscriptionProjector {
  static projectSubscriptionResponse(
    apiResponse: SubscriptionApiModels.GetSubscriptionResponse,
  ): SubscriptionModels.SubscriptionForm {
    return {
      address: AddressProjector.projectAddressResponse(apiResponse.address!),
      nomCabinet: apiResponse.name,
      numSiret: apiResponse.numSiret,
      employeeCount: apiResponse.employeeCount,
      outletCount: apiResponse.outletCount,
      turnover: apiResponse.turnover,
      civility: apiResponse.headOfAgency
        ? apiResponse.headOfAgency.civility
        : undefined,
      firstName: apiResponse.headOfAgency
        ? apiResponse.headOfAgency.firstName
        : undefined,
      lastName: apiResponse.headOfAgency
        ? apiResponse.headOfAgency.lastName
        : undefined,
      email: apiResponse.headOfAgency
        ? apiResponse.headOfAgency.email
        : undefined,
      phone: apiResponse.headOfAgency
        ? apiResponse.headOfAgency.phone
        : undefined,
      mobile: apiResponse.headOfAgency
        ? apiResponse.headOfAgency.mobile
        : undefined,
      isHeadOfAgency: apiResponse.isHeadOfAgency,
    };
  }

  static projectSaveSubscriptionRequest(
    form: SubscriptionModels.SubscriptionForm,
  ): SubscriptionApiModels.PostSubscriptionRequest {
    let postSubRequest: SubscriptionApiModels.PostSubscriptionRequest = {
      address: AddressProjector.projectAddressRequest(form.address!),
      name: form.nomCabinet,
      numSiret: form.numSiret,
      isHeadOfAgency: form.isHeadOfAgency,
      turnover: form.turnover,
      outletCount: form.outletCount,
      employeeCount: form.employeeCount,
      headOfAgency: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        mobile: form.mobile,
        phone: form.phone,
        civility: form.civility,
      },
    };

    return postSubRequest;
  }

  static projectBooleanToStatus(bool: boolean): CommonModels.StatusEnum {
    return bool
      ? CommonModels.StatusEnum.Valid
      : CommonModels.StatusEnum.PartiallyValid;
  }

  static projectDocumentInformationRequest(
    info: SubscriptionModels.SubscriptionDocumentInformation,
  ): SubscriptionApiModels.PostSubscriptionDocumentInformationsRequest {
    return {
      documentId: info.id !== -1 ? info.id : undefined,
      data: this.projectDocumentInformationDataRequest(info.data, info.type),
      rejects: info.rejects.filter((r) => r.active).map((r) => r.id),
      customRejects: info.customRejects
        .filter((cr) => cr.active)
        .map((cr) => cr.message),
      state: info.state,
      fragmentStates: info.fragments.map((frag) => {
        return { state: frag.state, id: frag.id };
      }),
    };
  }

  static projectDocumentInformationDataRequest(
    data: DocumentModels.DocumentInformation,
    type: DocumentModels.DocumentType,
  ) {
    switch (type) {
      case DocumentModels.DocumentType.Orias:
        const oriasData = <DocumentModels.OriasInformation>data;
        return Object.assign({}, oriasData, {
          civilResponsabilityEndValidity: DateProjector.fromAppToApi(
            oriasData.civilResponsabilityEndValidity,
          ),
        });
      case DocumentModels.DocumentType.KBIS:
        const kbisData = <DocumentModels.KbisInformation>data;
        return Object.assign({}, kbisData, {
          editionDate: DateProjector.fromAppToApi(kbisData.editionDate),
        });
    }

    return data;
  }

  static projectDocumentInformationResponse(
    info: SubscriptionApiModels.GetSubscriptionDocumentInformationsResponse,
    docType: DocumentModels.DocumentType,
  ): SubscriptionModels.SubscriptionDocumentInformation {
    if (info) {
      let result: SubscriptionModels.SubscriptionDocumentInformation | any = {
        id: info.id,
        customRejects: !info.customRejects
          ? []
          : info.customRejects.map((cr) => {
              return { active: true, message: cr };
            }),
        rejects: info.rejects,
        data: info.data
          ? this.projectDocumentInformationDataResponse(info.data, info.type)
          : {},
        state:
          info.fragments && info.fragments.length > 0
            ? info.state
            : DocumentModels.DocumentStateEnum.NotFound,
        type: info.type,
        fragments: info.fragments.map((frag) => {
          return {
            ...frag,
            creationDate: dateFromISOString(frag.creationDate),
          };
        }),
      };
      return result;
    }

    return {
      id: -1,
      rejects: [],
      data: {},
      type: docType,
      state: DocumentModels.DocumentStateEnum.NotFound,
      customRejects: [],
      fragments: [],
    };
  }

  static projectDocumentInformationDataResponse(
    data: DocumentModels.DocumentInformation,
    type: DocumentModels.DocumentType,
  ) {
    switch (type) {
      case DocumentModels.DocumentType.Orias:
        const oriasApiData = <DocumentModels.OriasInformation>data;
        return Object.assign({}, oriasApiData, {
          civilResponsabilityEndValidity: DateProjector.fromApiToApp(
            oriasApiData.civilResponsabilityEndValidity,
          ),
        });
      case DocumentModels.DocumentType.KBIS:
        const kbisData = <DocumentModels.KbisInformation>data;
        return Object.assign({}, kbisData, {
          editionDate: DateProjector.fromApiToApp(kbisData.editionDate),
        });
    }

    return data;
  }

  static projectDocument2(
    document: DocumentModels.Document2,
  ): DocumentModels.Document2 | any {
    return {
      ...document,
      fragments: document.fragments.map((frag) => {
        return {
          ...frag,
          creationDate: dateFromISOString(frag.creationDate.toString()),
        };
      }),
    };
  }
}
